import { Drawer } from '@mui/material';
import { CreateTile, Tile, TileWithIconUpload } from '@thro-types/users';

import { Formik } from 'formik';
import { omit } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useTilesMutation } from 'api/hooks/tiles';
import { TileType } from 'app/containers';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { selectTileDrawer } from 'app/containers/Dashboard/HomePage/selectors';
import {
  TileDrawerActions,
  tileDrawerReducer,
  drawerSliceKey,
  LayoutActions
} from 'app/containers/Dashboard/HomePage/tileDrawerSlice';
import { DrawerMode } from 'app/containers/Dashboard/HomePage/types';
import { translations } from 'locales/i18n';
import { useInjectReducer } from 'utils/redux-injectors';

import { CreateContent } from './components/CreateContent';
import { DefaultIconsContent } from './components/DefaultIconsContent';
import { PredefinedContent } from './components/PredefinedTilesContent';
import { uploadAttachmentToS3 } from './helper';
import { initialCardValues, tileValidationSchema } from './validation';

interface TileDrawerProps {
  createType: TileType;
}

export const TileDrawer = (props: TileDrawerProps) => {
  useInjectReducer({ key: drawerSliceKey, reducer: tileDrawerReducer });
  const { t } = useTranslation();
  const tileDrawer = useSelector(selectTileDrawer);
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const { creation, update } = useTilesMutation();

  const convertToTile = (createTile: CreateTile, id: number): Tile => {
    return {
      ...createTile,
      id: id
    };
  };

  useEffect(() => {
    if (tileDrawer.tile) {
      setIsEditing(Object.hasOwn(tileDrawer.tile, 'id'));
    }
  }, [tileDrawer.tile]);

  const handleSave = async (values: Tile | CreateTile | TileWithIconUpload) => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    if (Object.hasOwn(values, 'iconToUpload')) {
      try {
        const valuesWithIcon = values as TileWithIconUpload;
        let uploadResult = await uploadAttachmentToS3({
          file: valuesWithIcon.iconToUpload,
          icon: valuesWithIcon.icon
        });

        //@ts-ignore
        delete values.iconToUpload;
        values.icon = uploadResult;
      } catch (error) {
        dispatch(AlertActions.error(t(translations.common.alerts.failedImageUpload())));
        setSubmitting(false);
        return;
      }
    }

    if (!values.styleType?.length) {
      delete values.styleType;
    }

    if (isEditing && tileDrawer.tile) {
      try {
        const valuesWithoutId = omit(values, 'id');
        await update.mutateAsync({ id: tileDrawer.tile.id, data: valuesWithoutId });
        dispatch(AlertActions.success(t(translations.pages.home.alerts.successfulUpdate())));
      } catch (error) {
        dispatch(AlertActions.error(t(translations.pages.home.alerts.failedUpdate())));
      } finally {
        setSubmitting(false);
        dispatch(TileDrawerActions.close());
      }
    } else if ((!isEditing || !tileDrawer.tile) && TileType.CUSTOM) {
      try {
        if (values.type === TileType.SYSTEM || values.type === TileType.DEFAULT) {
          //System/Default Tiles will be created in database in the Admin view
          await creation.mutateAsync(values);
        }
        if (values.type === TileType.CUSTOM) {
          //Custom Tiles will be created later if the user saves his changes
          const newTile: Tile = convertToTile(values, 0);
          dispatch(
            LayoutActions.addTilePositionAtStart({
              id: new Date().valueOf(),
              position: 0,
              tile: newTile
            })
          );
        }
        dispatch(AlertActions.success(t(translations.pages.home.alerts.successfulCreate())));
      } catch (error) {
        dispatch(AlertActions.error(t(translations.pages.home.alerts.failedCreate())));
      } finally {
        setSubmitting(false);
        dispatch(TileDrawerActions.close());
      }
    }
  };

  const renderContent = () => {
    switch (tileDrawer.currentMode) {
      case DrawerMode.PREDEFINED:
        return <PredefinedContent />;
      case DrawerMode.CREATE:
        return <CreateContent type={props.createType} />;
      case DrawerMode.DEFAULTICONS:
        return <DefaultIconsContent />;
      default:
        break;
    }
  };
  return (
    <Drawer
      anchor="right"
      open={tileDrawer.open}
      onClose={() => dispatch(TileDrawerActions.close())}
      PaperProps={{
        sx: { width: { xs: '70%', md: '35%', xl: '25%' } }
      }}
    >
      <Formik
        initialValues={initialCardValues(props.createType, tileDrawer.tile)}
        onSubmit={values => handleSave(values)}
        validationSchema={tileValidationSchema(t)}
        validateOnMount
      >
        {renderContent()}
      </Formik>
    </Drawer>
  );
};
